import { ButtonBase } from '@mui/material';
import clsx from 'clsx';
import { useState } from 'react';

import { useParsedDirectiveRows } from './use-parsed-directive-rows.js';

import { DirectiveApplyBar } from './directive-apply-bar.js';

interface OcsRow {
	signalName: string;
	characteristic: string;
	minimum?: number | null;
	minimumRelativeSignal?: string | null;
	nominal?: number | null;
	nominalRelativeSignal?: string | null;
	maximum?: number | null;
	maximumRelativeSignal?: string | null;
}

export interface OcsTableMarkdownItemProps {
	/** Raw JSON string encoding an array of {@link OcsRow} entries. */
	jsonData?: string;
}

const DASH = '—';

const editInputClass =
	'w-full min-w-10 bg-transparent border-b border-gray-a6 focus:outline-none focus:border-blue-9 px-1 py-0.5';

function parseOcs(jsonData: string): OcsRow[] | null {
	try {
		const parsed: unknown = JSON.parse(jsonData);
		if (!Array.isArray(parsed)) {
			return null;
		}
		const rows = parsed.filter(
			(item): item is OcsRow =>
				typeof item === 'object' &&
				item !== null &&
				typeof (item as Record<string, unknown>)['signalName'] === 'string' &&
				typeof (item as Record<string, unknown>)['characteristic'] === 'string',
		);
		return rows.length > 0 ? rows : null;
	} catch {
		return null;
	}
}

/**
 * Markdown directive component that renders a compact operating conditions / specifications (OCS)
 * table inside a workbench message.
 *
 * Extends the AMR layout with an additional Nominal column between Min and Max. Parses `jsonData`
 * as `Array<OcsRow>` and shows one row per signal. Optional relative-signal annotations appear in
 * separate columns alongside each numeric value. Rows are editable inline before applying. Renders
 * `null` when `jsonData` is absent or not valid JSON.
 *
 * @param jsonData - Raw JSON string containing the OCS array.
 */
export function OcsTableMarkdownItem({ jsonData }: OcsTableMarkdownItemProps) {
	const [rows, setRows] = useParsedDirectiveRows(jsonData, parseOcs);
	const [isEditing, setIsEditing] = useState(false);

	if (!rows) {
		return null;
	}

	function updateRow(index: number, patch: Partial<OcsRow>) {
		setRows((prev) => (prev ? prev.map((r, i) => (i === index ? { ...r, ...patch } : r)) : null));
	}

	function parseNumericInput(value: string): number | null {
		if (value === '') {
			return null;
		}
		const n = Number(value);
		return Number.isFinite(n) ? n : null;
	}

	return (
		<>
			<div className='my-1 overflow-x-auto'>
				<table className='w-full border-collapse text-sm'>
					<thead>
						<tr className='border-b border-gray-a4'>
							<th className='min-w-24 px-2 py-1 text-left font-semibold whitespace-nowrap text-gray-11'>Signal</th>
							<th className='min-w-28 px-2 py-1 text-left font-semibold whitespace-nowrap text-gray-11'>
								Characteristic
							</th>
							<th className='min-w-16 px-2 py-1 text-left font-semibold whitespace-nowrap text-gray-11'>Min</th>
							<th className='min-w-16 px-2 py-1 text-left font-semibold whitespace-nowrap text-gray-11'>(rel.)</th>
							<th className='min-w-16 px-2 py-1 text-left font-semibold whitespace-nowrap text-gray-11'>Nominal</th>
							<th className='min-w-16 px-2 py-1 text-left font-semibold whitespace-nowrap text-gray-11'>(rel.)</th>
							<th className='min-w-16 px-2 py-1 text-left font-semibold whitespace-nowrap text-gray-11'>Max</th>
							<th className='min-w-16 px-2 py-1 text-left font-semibold whitespace-nowrap text-gray-11'>(rel.)</th>
							<th className='px-2 py-1 text-right'>
								<ButtonBase
									className='text-xs font-normal tracking-normal text-gray-11 normal-case hover:text-gray-12'
									onClick={() => setIsEditing((v) => !v)}
								>
									{isEditing ? 'Done' : 'Edit'}
								</ButtonBase>
							</th>
						</tr>
					</thead>
					<tbody>
						{rows.map((row, index) => (
							<tr
								key={index}
								className={clsx('border-b border-gray-a3', index % 2 === 0 ? 'bg-transparent' : 'bg-gray-a2')}
							>
								<td className='px-2 py-1 font-mono whitespace-nowrap text-gray-12'>
									{isEditing ?
										<input
											type='text'
											value={row.signalName}
											onChange={(e) => updateRow(index, { signalName: e.target.value })}
											className={editInputClass}
										/>
									:	row.signalName}
								</td>
								<td className='px-2 py-1 text-gray-12'>
									{isEditing ?
										<input
											type='text'
											value={row.characteristic}
											onChange={(e) => updateRow(index, { characteristic: e.target.value })}
											className={editInputClass}
										/>
									:	row.characteristic}
								</td>
								<td className='px-2 py-1 text-gray-12'>
									{isEditing ?
										<input
											type='number'
											value={row.minimum ?? ''}
											onChange={(e) => updateRow(index, { minimum: parseNumericInput(e.target.value) })}
											className={editInputClass}
										/>
									: row.minimum == null ?
										DASH
									:	String(row.minimum)}
								</td>
								<td className='px-2 py-1 text-gray-11'>
									{isEditing ?
										<input
											type='text'
											value={row.minimumRelativeSignal ?? ''}
											onChange={(e) => updateRow(index, { minimumRelativeSignal: e.target.value || null })}
											className={editInputClass}
										/>
									:	(row.minimumRelativeSignal ?? DASH)}
								</td>
								<td className='px-2 py-1 text-gray-12'>
									{isEditing ?
										<input
											type='number'
											value={row.nominal ?? ''}
											onChange={(e) => updateRow(index, { nominal: parseNumericInput(e.target.value) })}
											className={editInputClass}
										/>
									: row.nominal == null ?
										DASH
									:	String(row.nominal)}
								</td>
								<td className='px-2 py-1 text-gray-11'>
									{isEditing ?
										<input
											type='text'
											value={row.nominalRelativeSignal ?? ''}
											onChange={(e) => updateRow(index, { nominalRelativeSignal: e.target.value || null })}
											className={editInputClass}
										/>
									:	(row.nominalRelativeSignal ?? DASH)}
								</td>
								<td className='px-2 py-1 text-gray-12'>
									{isEditing ?
										<input
											type='number'
											value={row.maximum ?? ''}
											onChange={(e) => updateRow(index, { maximum: parseNumericInput(e.target.value) })}
											className={editInputClass}
										/>
									: row.maximum == null ?
										DASH
									:	String(row.maximum)}
								</td>
								<td className='px-2 py-1 text-gray-11'>
									{isEditing ?
										<input
											type='text'
											value={row.maximumRelativeSignal ?? ''}
											onChange={(e) => updateRow(index, { maximumRelativeSignal: e.target.value || null })}
											className={editInputClass}
										/>
									:	(row.maximumRelativeSignal ?? DASH)}
								</td>
								<td />
							</tr>
						))}
					</tbody>
				</table>
			</div>
			<DirectiveApplyBar data={rows} configId='boundaries-iec' />
		</>
	);
}
